import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/Alert';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import {
  Plus, Edit, Trash2, Eye, CheckCircle,
  AlertCircle, Users, Zap, Globe, Database,
  Search, X, Download, Settings, Shield, Key, Link, RefreshCw, Info, Star
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import toast from 'react-hot-toast';

const ThirdPartyApps = () => {
  const [apps, setApps] = useState([]);
  const [availableApps, setAvailableApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [installFormData, setInstallFormData] = useState({
    appName: '',
    appUrl: '',
    apiKey: '',
    apiSecret: '',
    webhookUrl: '',
    category: 'Communication',
    permissions: []
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    loadThirdPartyApps();
    loadAvailableApps();
  }, []);

  const loadThirdPartyApps = async () => {
    setLoading(true);
    try {
      // Use MockDataService if available, otherwise use mock data
      const response = await MockDataService.getThirdPartyApps?.() || { success: true, data: [] };
      if (response.success) {
        setApps(response.data || []);
      } else {
        // Fallback to mock data
        const mockApps = [
          {
            id: 'app-001',
            name: 'Slack Integration',
            description: 'Connect your helpdesk with Slack for seamless communication',
            status: 'active',
            category: 'Communication',
            version: '2.1.0',
            lastUpdated: '2024-01-15',
            permissions: ['read_tickets', 'create_tickets', 'update_tickets'],
            webhookUrl: 'https://hooks.slack.com/services/...',
            apiKey: 'sk-***...***',
            installedBy: 'admin@company.com',
            installDate: '2024-01-10'
          },
          {
            id: 'app-002',
            name: 'Microsoft Teams',
            description: 'Integrate with Microsoft Teams for notifications and ticket management',
            status: 'active',
            category: 'Communication',
            version: '1.8.2',
            lastUpdated: '2024-01-12',
            permissions: ['read_tickets', 'create_tickets'],
            webhookUrl: 'https://company.webhook.office.com/...',
            apiKey: 'ms-***...***',
            installedBy: 'admin@company.com',
            installDate: '2024-01-08'
          },
          {
            id: 'app-003',
            name: 'Zapier Connector',
            description: 'Automate workflows with Zapier integration',
            status: 'inactive',
            category: 'Automation',
            version: '3.0.1',
            lastUpdated: '2024-01-05',
            permissions: ['read_tickets', 'create_tickets', 'update_tickets', 'delete_tickets'],
            webhookUrl: 'https://hooks.zapier.com/hooks/...',
            apiKey: 'zp-***...***',
            installedBy: 'admin@company.com',
            installDate: '2023-12-20'
          },
          {
            id: 'app-004',
            name: 'Salesforce CRM',
            description: 'Sync customer data and tickets with Salesforce',
            status: 'active',
            category: 'CRM',
            version: '4.2.0',
            lastUpdated: '2024-01-14',
            permissions: ['read_tickets', 'create_tickets', 'update_tickets', 'read_customers'],
            webhookUrl: 'https://company.salesforce.com/services/...',
            apiKey: 'sf-***...***',
            installedBy: 'admin@company.com',
            installDate: '2024-01-01'
          }
        ];
        setApps(mockApps);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to load third-party apps');
      setLoading(false);
    }
  };

  const loadAvailableApps = async () => {
    try {
      // Mock available apps that can be installed
      const mockAvailableApps = [
        {
          id: 'available-slack',
          name: 'Slack',
          description: 'Integrate Slack workspace with your helpdesk for real-time notifications and ticket updates',
          category: 'Communication',
          icon: 'Users',
          rating: 4.8,
          installs: 1250,
          developer: 'Slack Technologies',
          version: '2.1.0',
          permissions: ['read_tickets', 'create_tickets', 'update_tickets']
        },
        {
          id: 'available-teams',
          name: 'Microsoft Teams',
          description: 'Connect Microsoft Teams for team collaboration and ticket management',
          category: 'Communication',
          icon: 'Users',
          rating: 4.6,
          installs: 890,
          developer: 'Microsoft',
          version: '1.8.2',
          permissions: ['read_tickets', 'create_tickets']
        },
        {
          id: 'available-zapier',
          name: 'Zapier',
          description: 'Automate your helpdesk workflows with thousands of app integrations',
          category: 'Automation',
          icon: 'Zap',
          rating: 4.9,
          installs: 2100,
          developer: 'Zapier Inc.',
          version: '3.0.1',
          permissions: ['read_tickets', 'create_tickets', 'update_tickets', 'delete_tickets']
        },
        {
          id: 'available-salesforce',
          name: 'Salesforce CRM',
          description: 'Sync tickets and customer data with Salesforce CRM platform',
          category: 'CRM',
          icon: 'Database',
          rating: 4.7,
          installs: 1560,
          developer: 'Salesforce',
          version: '4.2.0',
          permissions: ['read_tickets', 'create_tickets', 'update_tickets', 'read_customers']
        },
        {
          id: 'available-jira',
          name: 'Jira Integration',
          description: 'Link helpdesk tickets with Jira issues for project management',
          category: 'Project Management',
          icon: 'Globe',
          rating: 4.5,
          installs: 720,
          developer: 'Atlassian',
          version: '1.5.3',
          permissions: ['read_tickets', 'create_tickets', 'update_tickets']
        },
        {
          id: 'available-hubspot',
          name: 'HubSpot CRM',
          description: 'Connect HubSpot CRM for comprehensive customer relationship management',
          category: 'CRM',
          icon: 'Database',
          rating: 4.6,
          installs: 980,
          developer: 'HubSpot',
          version: '2.3.1',
          permissions: ['read_tickets', 'create_tickets', 'update_tickets', 'read_customers', 'update_customers']
        }
      ];
      setAvailableApps(mockAvailableApps);
    } catch (err) {
      console.error('Error loading available apps:', err);
    }
  };

  const handleInstallApp = (app = null) => {
    if (app) {
      setSelectedApp(app);
      setInstallFormData({
        appName: app.name,
        appUrl: '',
        apiKey: '',
        apiSecret: '',
        webhookUrl: '',
        category: app.category,
        permissions: app.permissions || []
      });
    } else {
      setSelectedApp(null);
      setInstallFormData({
        appName: '',
        appUrl: '',
        apiKey: '',
        apiSecret: '',
        webhookUrl: '',
        category: 'Communication',
        permissions: []
      });
    }
    setShowInstallModal(true);
  };

  const handleInstallSubmit = async () => {
    if (!installFormData.appName.trim()) {
      toast.error('App name is required');
      return;
    }

    try {
      setInstalling(true);
      
      // Simulate installation
      const newApp = {
        id: `app-${Date.now()}`,
        name: installFormData.appName,
        description: selectedApp?.description || 'Custom third-party integration',
        status: 'active',
        category: installFormData.category,
        version: selectedApp?.version || '1.0.0',
        lastUpdated: new Date().toISOString().split('T')[0],
        permissions: installFormData.permissions,
        webhookUrl: installFormData.webhookUrl || 'N/A',
        apiKey: installFormData.apiKey ? `${installFormData.apiKey.substring(0, 3)}***...***` : 'N/A',
        installedBy: 'admin@company.com',
        installDate: new Date().toISOString().split('T')[0]
      };

      // Add to installed apps
      setApps([...apps, newApp]);
      setShowInstallModal(false);
      setSuccess(`${installFormData.appName} installed successfully`);
      toast.success(`${installFormData.appName} installed successfully`);
      
      // Reset form
      setInstallFormData({
        appName: '',
        appUrl: '',
        apiKey: '',
        apiSecret: '',
        webhookUrl: '',
        category: 'Communication',
        permissions: []
      });
      setSelectedApp(null);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      toast.error('Failed to install app');
      setError('Failed to install app');
      setTimeout(() => setError(''), 3000);
    } finally {
      setInstalling(false);
    }
  };

  const handleUpdateApp = (appId) => {
    setSuccess(`App ${appId} updated successfully`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleUninstallApp = (appId) => {
    setApps(apps.filter(app => app.id !== appId));
    setSuccess(`App ${appId} uninstalled successfully`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Communication': return Users;
      case 'Automation': return Zap;
      case 'CRM': return Database;
      case 'Project Management': return Globe;
      default: return Globe;
    }
  };

  const getCategoryIconComponent = (category) => {
    const IconComponent = getCategoryIcon(category);
    return <IconComponent className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading third-party apps...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Third-Party Apps</h1>
          <p className="mt-2 text-gray-600">Manage integrations with external applications</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => handleInstallApp(null)} 
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md"
           style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Install New App
          </Button>
          <Button 
            onClick={loadThirdPartyApps}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">Error</AlertTitle>
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Success</AlertTitle>
          <AlertDescription className="text-green-700">{success}</AlertDescription>
        </Alert>
      )}

      {/* Apps Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {apps.map((app) => (
          <Card key={app.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    {getCategoryIconComponent(app.category)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{app.name}</CardTitle>
                    <p className="text-sm text-gray-600">{app.category}</p>
                  </div>
                </div>
                <Badge className={getStatusColor(app.status)}>
                  {app.status}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-700">{app.description}</p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Version:</span>
                  <span className="font-medium">{app.version}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="font-medium">{app.lastUpdated}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Installed:</span>
                  <span className="font-medium">{app.installDate}</span>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Permissions:</p>
                <div className="flex flex-wrap gap-1">
                  {app.permissions.map((permission, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {permission.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex space-x-2 pt-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleUpdateApp(app.id)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleUninstallApp(app.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {apps.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Third-Party Apps</h3>
            <p className="text-gray-600 mb-4">Get started by installing your first integration</p>
            <Button onClick={() => handleInstallApp(null)} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md"
              style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}>
              <Plus className="w-4 h-4 mr-2" />
              Install App
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Install New App Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white shadow-xl">
            <CardHeader className="border-b border-gray-200">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-xl font-semibold text-gray-900">
                  <Download className="h-5 w-5" />
                  {selectedApp ? `Install ${selectedApp.name}` : 'Install New Third-Party App'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowInstallModal(false);
                    setSelectedApp(null);
                    setInstallFormData({
                      appName: '',
                      appUrl: '',
                      apiKey: '',
                      apiSecret: '',
                      webhookUrl: '',
                      category: 'Communication',
                      permissions: []
                    });
                  }}
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Available Apps Browser */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2">Browse Available Apps</Label>
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search apps..."
                        value={searchQuery}
                        onChange={(value) => setSearchQuery(value)}
                        className="pl-10"
                      />
                    </div>
                    <Select
                      value={filterCategory}
                      onValueChange={setFilterCategory}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="Communication">Communication</SelectItem>
                        <SelectItem value="Automation">Automation</SelectItem>
                        <SelectItem value="CRM">CRM</SelectItem>
                        <SelectItem value="Project Management">Project Management</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-2">
                    {availableApps
                      .filter(app => {
                        const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                           app.description.toLowerCase().includes(searchQuery.toLowerCase());
                        const matchesCategory = filterCategory === 'all' || app.category === filterCategory;
                        return matchesSearch && matchesCategory;
                      })
                      .map(app => (
                        <Card
                          key={app.id}
                          className={`cursor-pointer hover:border-blue-500 transition-colors ${
                            selectedApp?.id === app.id ? 'border-blue-500 bg-blue-50' : ''
                          }`}
                          onClick={() => handleInstallApp(app)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{app.name}</h4>
                                <p className="text-xs text-gray-600 mt-1">{app.description}</p>
                                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                  <span>{app.category}</span>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                    {app.rating}
                                  </span>
                                  <span>•</span>
                                  <span>{app.installs.toLocaleString()} installs</span>
                                </div>
                              </div>
                              {React.createElement(getCategoryIcon(app.category), { className: "h-5 w-5 text-blue-600 ml-2" })}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    {availableApps.filter(app => {
                      const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                         app.description.toLowerCase().includes(searchQuery.toLowerCase());
                      const matchesCategory = filterCategory === 'all' || app.category === filterCategory;
                      return matchesSearch && matchesCategory;
                    }).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p>No apps found</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Installation Form */}
                <div className="space-y-4 border-l pl-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Installation Details</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="app-name" className="text-sm font-medium text-gray-700">
                          App Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="app-name"
                          placeholder="Enter app name"
                          value={installFormData.appName}
                          onChange={(value) => setInstallFormData({ ...installFormData, appName: value })}
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="app-category" className="text-sm font-medium text-gray-700">
                          Category
                        </Label>
                        <Select
                          value={installFormData.category}
                          onValueChange={(value) => setInstallFormData({ ...installFormData, category: value })}
                        >
                          <SelectTrigger id="app-category" className="w-full">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Communication">Communication</SelectItem>
                            <SelectItem value="Automation">Automation</SelectItem>
                            <SelectItem value="CRM">CRM</SelectItem>
                            <SelectItem value="Project Management">Project Management</SelectItem>
                            <SelectItem value="Analytics">Analytics</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="app-url" className="text-sm font-medium text-gray-700">
                          App URL
                        </Label>
                        <Input
                          id="app-url"
                          type="url"
                          placeholder="https://app.example.com"
                          value={installFormData.appUrl}
                          onChange={(value) => setInstallFormData({ ...installFormData, appUrl: value })}
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="api-key" className="text-sm font-medium text-gray-700">
                          API Key
                        </Label>
                        <Input
                          id="api-key"
                          type="password"
                          placeholder="Enter API key"
                          value={installFormData.apiKey}
                          onChange={(value) => setInstallFormData({ ...installFormData, apiKey: value })}
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="api-secret" className="text-sm font-medium text-gray-700">
                          API Secret
                        </Label>
                        <Input
                          id="api-secret"
                          type="password"
                          placeholder="Enter API secret"
                          value={installFormData.apiSecret}
                          onChange={(value) => setInstallFormData({ ...installFormData, apiSecret: value })}
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="webhook-url" className="text-sm font-medium text-gray-700">
                          Webhook URL
                        </Label>
                        <Input
                          id="webhook-url"
                          type="url"
                          placeholder="https://webhook.example.com/endpoint"
                          value={installFormData.webhookUrl}
                          onChange={(value) => setInstallFormData({ ...installFormData, webhookUrl: value })}
                          className="w-full"
                        />
                      </div>

                      {selectedApp && selectedApp.permissions && selectedApp.permissions.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700">Required Permissions</Label>
                          <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-md">
                            {selectedApp.permissions.map((permission, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {permission.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">Installation Note</p>
                            <p className="text-blue-700">
                              {selectedApp 
                                ? `Installing ${selectedApp.name} will grant the following permissions: ${selectedApp.permissions?.join(', ') || 'No special permissions'}`
                                : 'Enter the app details and configure the integration settings. Make sure all API credentials are valid.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowInstallModal(false);
                        setSelectedApp(null);
                        setInstallFormData({
                          appName: '',
                          appUrl: '',
                          apiKey: '',
                          apiSecret: '',
                          webhookUrl: '',
                          category: 'Communication',
                          permissions: []
                        });
                      }}
                      disabled={installing}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleInstallSubmit}
                      disabled={installing || !installFormData.appName.trim()}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {installing ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Installing...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Install App
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ThirdPartyApps;
